import { Copy, MessageCircle, Share2, Send } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

// Simple inline SVG icons for social platforms not in lucide
const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const DiscordIcon = () => (
  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
  </svg>
);

interface ShareMenuProps {
  /** Full URL to share */
  url: string;
  /** Text for the share message */
  text: string;
  /** Icon button class overrides */
  buttonClassName?: string;
  /** Icon size class */
  iconClassName?: string;
  /** Tooltip side */
  tooltipSide?: "top" | "bottom" | "left" | "right";
}

const ShareMenu = ({
  url,
  text,
  buttonClassName = "p-1 hover:scale-110 transition-all",
  iconClassName = "w-3.5 h-3.5 text-blue-400/70 hover:text-blue-400",
  tooltipSide = "top",
}: ShareMenuProps) => {
  const encodedUrl = encodeURIComponent(url);
  const encodedText = encodeURIComponent(text);

  const handleCopy = () => {
    navigator.clipboard.writeText(url).then(() => toast({ title: "Link copiado!" }));
  };

  const options = [
    {
      label: "Copiar link",
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopy,
      color: "text-foreground",
    },
    {
      label: "WhatsApp",
      icon: <MessageCircle className="w-4 h-4" />,
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      color: "text-green-500",
    },
    {
      label: "Telegram",
      icon: <Send className="w-4 h-4" />,
      href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      color: "text-sky-400",
    },
    {
      label: "Facebook",
      icon: <FacebookIcon />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "text-blue-500",
    },
    {
      label: "Twitter / X",
      icon: <XIcon />,
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      color: "text-foreground",
    },
  ];

  return (
    <Popover>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              className={buttonClassName}
            >
              <Share2 className={iconClassName} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide} className="z-[100]">Compartilhar</TooltipContent>
      </Tooltip>

      <PopoverContent
        className="w-44 p-1.5 tibia-border bg-card z-[100]"
        side="top"
        align="center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-0.5">
          {options.map((opt) =>
            opt.href ? (
              <a
                key={opt.label}
                href={opt.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-[10px] font-pixel hover:bg-secondary/50 transition-colors ${opt.color}`}
              >
                {opt.icon}
                {opt.label}
              </a>
            ) : (
              <button
                key={opt.label}
                onClick={opt.onClick}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-[10px] font-pixel hover:bg-secondary/50 transition-colors text-left ${opt.color}`}
              >
                {opt.icon}
                {opt.label}
              </button>
            )
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareMenu;
